import {hasQuotePermission} from '../../../utils/quote-access';
import {Permissions} from '../../../enums/permissions';
import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
  WritableSignal,
  DestroyRef
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {JobsService} from "../../../services/jobs.service";
import {ActivatedRoute, Router} from "@angular/router";
import {SessionService} from "../../../services/session.service";
import {ToastService} from "../../../services/toast.service";
import {DateUtils} from "../../../utils/date-utils";
import {TableParams} from "../../../interfaces/table/table-params";
import {constructJobsColumns, JobsTableColumn} from "./jobs-table.config";
import {TableColumn} from "../../../interfaces/table/table-column";
import {auditTime, catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap} from "rxjs";
import {FormBuilder, FormGroup} from "@angular/forms";
import {TableData} from "../../../interfaces/table/table-data";
import {ObjectUtils} from "../../../utils/object-utils";
import {StatusCategory} from "../../../enums/status-category";

@Component({
  selector: 'app-jobs',
  templateUrl: './jobs.component.html',
  styleUrls: ['./jobs.component.css'],
  standalone: false
})
export class JobsComponent implements OnInit {
  readonly allJobType = {job_type_id: 0, job_type_name: 'All Job Types'};
  jobTypes: any[] = [this.allJobType];
  readonly allJobStatus = {job_status_id: 0, job_status_name: 'All Job Statuses'};
  jobStatuses: any[] = [this.allJobStatus];
  readonly allLocationsOption = {location_id: 0, location_name: 'All Locations'};
  locations: any[] = [this.allLocationsOption];
  filterForm!: FormGroup;
  currentUser = signal<any>(null);
  isExportDropdownOpen: boolean = false;
  isGeneratingReport: boolean = false;
  currentFileType: string = '';
  activeTab = signal<'pending' | 'completed'>('pending');
  readonly unexpandedAssetsCount = 3;
  expandedAssets = new Set<string>();

  jobsTableColumns = computed<TableColumn[]>(() => {
    const showQuoteNumber = this.currentUser()?.is_owner == 1 && this.currentUser()?.is_rentee == 0
      && hasQuotePermission(this.currentUser(), Permissions.ViewQuotes);
    const showCustomFields = this.currentUser()?.show_custom_fields;
    return constructJobsColumns(this.jobNumberColumn, this.assetColumn, showQuoteNumber, showCustomFields);
  });
  @ViewChild('jobNumberColumn', {static: true}) jobNumberColumn!: TemplateRef<any>;
  @ViewChild('assetColumn', {static: true}) assetColumn!: TemplateRef<any>;

  pendingTableData = signal<TableData>({data: [], total: 0});
  loadingPendingTableData = signal<boolean>(true);
  loadPendingJobs$ = new Subject<{ tableParams: TableParams, filterParams: any }>();
  pendingTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  currentPendingTablePage = signal<number>(1);

  completedTableData = signal<TableData>({data: [], total: 0});
  loadingCompletedTableData = signal<boolean>(true);
  loadCompletedJobs$ = new Subject<{ tableParams: TableParams, filterParams: any }>();
  completedTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  currentCompletedTablePage = signal<number>(1);

  protected readonly JobsTableColumn = JobsTableColumn;
  private readonly jobService = inject(JobsService);
  private readonly router = inject(Router);
  private readonly activeRouter = inject(ActivatedRoute);
  private readonly sessionService = inject(SessionService);
  private readonly toastService = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.sessionService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.currentUser.set(res.user));
  }

  ngOnInit() {
    this.initFilterForm();

    this.initJobsObservable(
      this.loadPendingJobs$,
      this.pendingTableData,
      this.loadingPendingTableData,
      StatusCategory.Pending,
      this.completedTableData,
      this.loadingCompletedTableData,
      'completed'
    );
    this.initJobsObservable(
      this.loadCompletedJobs$,
      this.completedTableData,
      this.loadingCompletedTableData,
      StatusCategory.Completed,
      this.pendingTableData,
      this.loadingPendingTableData,
      'pending'
    );

    this.getJobStatuses();
    this.getJobTypes();
    this.getLocations();

    this.refreshData();
  }

  initFilterForm() {
    this.filterForm = this.fb.group({
      search: [''],
      type: [0],
      status: [0],
      location: [0],
    });

    this.filterForm.get('search')!.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.currentPendingTablePage.set(1);
      this.currentCompletedTablePage.set(1);
      this.pendingTableParams = {...this.pendingTableParams, currentPage: 1};
      this.completedTableParams = {...this.completedTableParams, currentPage: 1};
      this.refreshData();
    });

    this.filterForm.valueChanges.pipe(
      distinctUntilChanged((prev, curr) =>
        prev.type === curr.type && prev.status === curr.status && prev.location === curr.location),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.currentPendingTablePage.set(1);
      this.currentCompletedTablePage.set(1);
      this.pendingTableParams = {...this.pendingTableParams, currentPage: 1};
      this.completedTableParams = {...this.completedTableParams, currentPage: 1};
      this.refreshData();
    });
  }

  async getJobTypes() {
    await this.jobService.getJobTypes().then((data: any) => {
      this.jobTypes = [this.allJobType, ...data];
    });
  }

  async getJobStatuses() {
    await this.jobService.getJobStatuses().then((statuses: any) => {
      this.jobStatuses = [this.allJobStatus, ...statuses];

      const queryStatusFilterName = this.activeRouter.snapshot.queryParams['status'];
      if (!queryStatusFilterName) return;

      const status = statuses.find((item: any) =>
        item.job_status_name.replaceAll(' ', '_').toLowerCase().includes(queryStatusFilterName));
      if (status) {
        this.filterForm.get('status')!.setValue(status.job_status_id);
      }
    });
  }

  async getLocations() {
    await this.jobService.getLocations().then((data: any) => {
      this.locations = [this.allLocationsOption, ...data];
    });
  }

  initJobsObservable(
    subject: Subject<{ tableParams: TableParams, filterParams: any }>,
    tableDataSignal: WritableSignal<TableData>,
    loadingSignal: WritableSignal<boolean>,
    statusCategoryId: string,
    altTableDataSignal: WritableSignal<TableData>,
    altLoadingSignal: WritableSignal<boolean>,
    altTab: 'pending' | 'completed') {
    subject.pipe(
      distinctUntilChanged((prev, curr) => ObjectUtils.deepEqual(prev, curr)),
      tap(_ => loadingSignal.set(true)),
      auditTime(0),
      switchMap(params => {
        const {tableParams, filterParams} = params;
        const offset = (tableParams.currentPage - 1) * tableParams.pageSize;
        return this.jobService.getJobs(
          tableParams.pageSize,
          offset,
          filterParams.search,
          tableParams.sortParams,
          filterParams.type,
          filterParams.status,
          statusCategoryId,
          filterParams.location);
      }),
      catchError(() => {
        loadingSignal.set(false);
        this.toastService.showToast('Failed to load service schedule', 'error');
        return of({status: 'error', data: {Data: []}, errors: []});
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      const data = response;
      if (!Array.isArray(data)) {
        console.error('API returned non-array data:', data);
        tableDataSignal.set({data: [], total: 0});
        return;
      }

      const jobs = data.map((item: any) => {
        let pick_ticket_number = '--';
        let cr_number = '--'
        if (item.CustomFields != null) {
          let custom_fields = JSON.parse(item.CustomFields);
          pick_ticket_number = custom_fields.filter((x: any) => x.field_name == 'pick_ticket_number')[0]?.value;
          cr_number = custom_fields.filter((x: any) => x.field_name == 'CRNumber')[0]?.value;
        }
        return {
          job_id: item.JobId,
          job_number: item.JobNumber,
          job_type: item.JobType,
          job_status: {name: item.JobStatus, color: item.StatusColor},
          asset: item.Assets,
          location: item.Location,
          due_date: DateUtils.formatDate(item.DueDate),
          quote_number: item.QuoteNumber,
          pick_ticket_number: pick_ticket_number,
          renew_pick_ticket_number: item.RenewPickTicketNumber == 0 ? null : item.RenewPickTicketNumber,
          cr_number: cr_number,
          order_number: item.OrderNumber,
          return_tracking_number: item.ReturnTrackingNumber
        };
      });

      const totalCount = data.length > 0 ? data[0].TotalCount : 0;
      if (data.length > 0 && data[0].StatusCategoryId !== statusCategoryId) {
        this.activeTab.set(altTab);
        tableDataSignal.set({data: [], total: 0});
        altTableDataSignal.set({data: jobs, total: totalCount});
        altLoadingSignal.set(false);
      } else {
        tableDataSignal.set({data: jobs, total: totalCount});
      }
      loadingSignal.set(false);
    });
  }

  isSelectClearable(controlName: string) {
    return this.filterForm.get(controlName)?.value !== 0;
  }

  clearSelectValue(controlName: string) {
    this.filterForm.get(controlName)?.setValue(0);
  }

  toggleExportDropdown() {
    this.isExportDropdownOpen = !this.isExportDropdownOpen;
  }

  closeExportDropdown() {
    this.isExportDropdownOpen = false;
  }

  exportMyJobs(fileType: 'csv' | 'excel', isRental: boolean) {
    this.isExportDropdownOpen = false;
    this.isGeneratingReport = true;
    this.currentFileType = fileType;

    const filterValues = this.filterForm.value;
    const requestPayload = {
      Search: filterValues.search || '',
      JobTypeId: filterValues.type || null,
      StatusId: filterValues.status || null,
      LocationId: filterValues.location || null,
      FileType: fileType,
    };

    const apiCall = isRental
      ? this.jobService.getJobsRentalExportData(
        requestPayload.JobTypeId,
        requestPayload.Search,
        requestPayload.StatusId,
        requestPayload.LocationId,
        requestPayload.FileType)
      : this.jobService.getJobsExportData(
        requestPayload.JobTypeId,
        requestPayload.Search,
        requestPayload.StatusId,
        requestPayload.LocationId,
        requestPayload.FileType
      );

    apiCall.then((response: any) => {
      if (response && response.downloadUrl) {
        window.open(response.downloadUrl, '_blank');
      } else {
        this.toastService.showToast('Failed to generate the report. Please try again later.', 'error');
      }
    }).catch(() => {
      this.toastService.showToast('Failed to generate the report. Please try again later.', 'error');
    }).finally(() => {
      this.isGeneratingReport = false;
    });
  }

  async handleExport(fileType: 'csv' | 'excel') {
    const is_owner = this.currentUser().is_owner;
    const is_rentee = this.currentUser().is_rentee;
    const anyJobs = this.pendingTableData().total! > 0 || this.completedTableData().total! > 0;

    if (!anyJobs) {
      this.toastService.showToast("No Jobs were found that match this filter", "error");
      return;
    }

    if (is_owner == 1 && is_rentee == 0) {
      this.exportMyJobs(fileType, false);
    } else if (is_owner == 0 && is_rentee == 1) {
      this.exportMyJobs(fileType, true);
    } else {
      this.exportMyJobs(fileType, false);
    }
  }

  changeTab(tab: 'pending' | 'completed') {
    this.activeTab.set(tab);
    this.refreshData();
  }

  handleItemClicked($event: any) {
    let job_id = $event.job_id;
    this.router.navigate(['/job'], {queryParams: {job_id: job_id}});
  }

  refreshData() {
    const filterParams = {...this.filterForm.value};
    if (this.activeTab() === 'pending') {
      const tableParams = {...this.pendingTableParams};
      this.loadPendingJobs$.next({tableParams, filterParams});
    } else {
      const tableParams = {...this.completedTableParams};
      this.loadCompletedJobs$.next({tableParams, filterParams});
    }
  }

  handlePendingJobsParamsChange(params: TableParams) {
    this.pendingTableParams = params;
    const filterParams = {...this.filterForm.value};
    this.loadPendingJobs$.next({tableParams: params, filterParams});
  }

  handleCompletedJobsParamsChange(params: TableParams) {
    this.completedTableParams = params;
    const filterParams = {...this.filterForm.value};
    this.loadCompletedJobs$.next({tableParams: params, filterParams});
  }

  getVisibleAssets(items: any[]): any[] {
    return items.slice(0, this.unexpandedAssetsCount);
  }

  getHiddenAssets(items: any[]): any[] {
    return items.slice(this.unexpandedAssetsCount);
  }

  toggleExpandedAssets(rowId: string, value: boolean) {
    if (value) {
      this.expandedAssets.delete(rowId);
    } else {
      this.expandedAssets.add(rowId);
    }
  }

  isAssetExpanded(rowId: string): boolean {
    return this.expandedAssets.has(rowId);
  }
}
